import React, { useMemo, useState } from "react";

import {
  faKey,
  faPlus,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { useCredentials } from "src/api/agents/credentials";
import { Card } from "src/base-components/Card";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import type { CredentialItem } from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { SubHeader } from "src/flow/SubHeader";
import { UserFromId } from "src/flow/User";
import { PROVIDER_DISPLAY_NAMES } from "src/genai/ModelSelector/ModelIcon";
import { DashboardContent } from "src/layout/DashboardContent";
import { CredentialActions } from "src/settings/credentials/CredentialActions";
import { DeleteCredentialModal } from "src/settings/credentials/DeleteCredentialModal";
import { TestCredentialModal } from "src/settings/credentials/TestCredentialModal";
import { UpsertCredentialModal } from "src/settings/credentials/UpsertCredentialModal";
import { dateFromNow, formatDate } from "src/utils/datetime";
import { errorMessage } from "src/utils/stringUtils";

const columnHelper = createColumnHelper<CredentialItem>();

type CallbackWithCredential = (credential: CredentialItem) => void;

const buildColumns = (
  onEdit: CallbackWithCredential,
  onDelete: CallbackWithCredential,
  onTest: CallbackWithCredential,
) => [
  columnHelper.accessor("name", {
    header: (info) => <Header info={info}>Name</Header>,
    cell: (info) => <Cell info={info}>{info.getValue()}</Cell>,
  }),
  columnHelper.accessor("provider", {
    header: (info) => <Header info={info}>Provider</Header>,
    cell: (info) => (
      <Cell info={info}>
        {PROVIDER_DISPLAY_NAMES[info.getValue()] || info.getValue()}
      </Cell>
    ),
  }),
  columnHelper.accessor("updated_by", {
    header: (info) => <Header info={info}>Last updated by</Header>,
    cell: (info) => {
      const userId = info.getValue();
      return (
        <Cell info={info}>
          {userId ? <UserFromId userId={userId} /> : <span>-</span>}
        </Cell>
      );
    },
  }),
  columnHelper.accessor("last_updated", {
    header: (info) => <Header info={info}>Last updated</Header>,
    cell: (info) => {
      const isoDate = info.getValue();
      if (!isoDate) {
        return <Cell info={info}>Never</Cell>;
      }
      const fromNow = dateFromNow(isoDate);
      const detailDate = formatDate(isoDate, "dd MMM yy HH:mm:ss");
      return (
        <Cell info={info}>
          <Tooltip placement="top" title={detailDate}>
            <span>{fromNow}</span>
          </Tooltip>
        </Cell>
      );
    },
  }),
  columnHelper.display({
    id: "actions",
    header: (info) => <Header info={info} />,
    cell: (info) => {
      const credential = info.row.original;
      return (
        <Cell className="justify-end" info={info}>
          <CredentialActions
            credential={credential}
            onDeleteClick={() => onDelete(credential)}
            onEditClick={() => onEdit(credential)}
            onTestClick={() => onTest(credential)}
          />
        </Cell>
      );
    },
    size: 100,
    minSize: 100,
    maxSize: 100,
    meta: {
      sticky: "right",
    },
  }),
];

export const AgentCredentialsOverview: React.FC = () => {
  const { isLoading, data, isError, error } = useCredentials();

  const [credentialToEdit, setCredentialToEdit] = useState<CredentialItem>();
  const [credentialToDelete, setCredentialToDelete] =
    useState<CredentialItem>();
  const [credentialToTest, setCredentialToTest] = useState<CredentialItem>();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const credentials = data?.credentials ?? [];

  const columns = useMemo(
    () =>
      buildColumns(
        setCredentialToEdit,
        setCredentialToDelete,
        setCredentialToTest,
      ),
    [],
  );

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          dataLoc="credentials-error-state"
          description={errorMessage(error)}
          headline="Error loading AI credentials"
          icon={faTriangleExclamation}
          variant="error"
        />
      );
    }

    if (!isLoading && credentials.length === 0) {
      return (
        <EmptyState
          actionButton={{
            label: "Add credential",
            icon: faPlus,
            onClick: () => setCreateModalOpen(true),
          }}
          dataLoc="credentials-empty-state"
          description="Add your own AI credentials to use them with Agents"
          headline="No AI credentials configured"
          icon={faKey}
        />
      );
    }

    return <Table columns={columns} data={credentials} isLoading={isLoading} />;
  };

  return (
    <DashboardContent
      Header={
        <SubHeader title="AI credentials">
          <SubHeader.Button
            dataLoc="add-credential-button"
            icon={faPlus}
            tooltip="Add credential"
            onClick={() => setCreateModalOpen(true)}
          />
        </SubHeader>
      }
    >
      <Card className="flex h-full flex-col pt-1" variant="inverted" fullWidth>
        <Card.Content className="h-full min-h-0 flex-1">
          {renderContent()}
        </Card.Content>
      </Card>

      <UpsertCredentialModal
        credential={credentialToEdit}
        open={createModalOpen || credentialToEdit !== undefined}
        onClose={() => {
          setCreateModalOpen(false);
          setCredentialToEdit(undefined);
        }}
      />

      <DeleteCredentialModal
        credential={credentialToDelete}
        onClose={() => setCredentialToDelete(undefined)}
      />

      <TestCredentialModal
        credential={credentialToTest}
        onClose={() => setCredentialToTest(undefined)}
      />
    </DashboardContent>
  );
};
